import { RichCheckboxsetWebElementBase } from './RichCheckboxsetWebElementBase';
import { By, error, WebElement } from 'selenium-webdriver';

type CardInputs = {
  card: WebElement;
  input: WebElement;
};

/**
 * The component WebElement for [oj-c-rich-checkboxset](../../jsdocs/oj-c.RichCheckboxset.html).
 * Do not instantiate this class directly, instead, use
 * [findRichCheckboxset](../functions/findRichCheckboxset.html).
 */
export class RichCheckboxsetWebElement extends RichCheckboxsetWebElementBase {
  /**
   * Sets the value of "value" property for the RichCheckboxset component
   * @param value The value to set for "value"
   * @throws {ElementNotInteractableError} if the API is called when the control is readonly or disabled
   */
  public async changeValue(value: Array<string | number> | null): Promise<void> {
    // Check if the control is interactable
    if (!(await this.isInteractable())) {
      // Cannot interact when the control is disabled or readonly
      throw new error.ElementNotInteractableError(
        'oj-c-rich-checkboxset: Value cannot be changed when the control is disabled or readonly'
      );
    }

    await this.whenBusyContextReady();
    await this.toggleValues(value);
  }

  /**
   * Checks if the Checkboxset is in interactable state
   */
  private async isInteractable(): Promise<boolean> {
    const isReadonly = await this.getReadonly();
    const isDisabled = await this.getDisabled();
    return !(isReadonly || isDisabled);
  }

  private async getInputsWithCards() {
    const cards = await this.findElements(By.xpath('//*[@role="listitem"]'));
    const cardsAndInputs: Array<CardInputs> = [];
    const promises = cards.map(async (card) => {
      const input = await card.findElement(By.css('input'));
      cardsAndInputs.push({
        card,
        input
      });
    });

    await Promise.all(promises);
    return cardsAndInputs;
  }

  /**
   * Check checkboxes that have value within values array
   */
  private async toggleValues(values: Array<string | number> | null) {
    const inputs = await this.getInputsWithCards();

    const promises = inputs.map(async ({ card, input }) => {
      const value = await input.getAttribute('value');
      const isSelected = await input.isSelected();
      const isInValues = values?.includes(value);

      if ((isInValues && !isSelected) || (!isInValues && isSelected)) {
        // toggle checkbox
        await card.click();
      }
    });

    await Promise.all(promises);
  }
}
